// budgetVsCommitment.js
// Single Source of Truth for Direct/Indirect Budget vs Commitment key
// resolution (Phase PO-3/PO-4 architectural consolidation).
// Consumed by PurchaseOrders.jsx and PrintDocument.jsx — any future fix
// or extension to this logic must be made here ONLY, not duplicated.

// Shared normalization helper — LOWER(TRIM()) standard established
// across the codebase (get_commitment_by_subcode RPC,
// PurchaseRequisitions.jsx Phase B, PO-1 DB triggers).
export const normSC = s => (s || '').toString().trim().toLowerCase()

// Direct: sub_code (text) -> resolved aggregation key.
// kind: 'resource' (Level 4 Resource Code found -- has a real
// tender_direct_items.resource_code match), 'legacy' (parent is Level 1
// directly -- pre-Resource-Code cost code, no budget mapping exists),
// or 'unknown' (sub_code not found in cost_codes at all -- data error).
// maps: { byId: Map<id, costCodeRow>, byNormCode: Map<normalizedCode, costCodeRow> }
export function resolveDirectKey(subCode, maps) {
  const sub = maps.byNormCode.get(normSC(subCode))
  if (!sub) return { key: null, kind: 'unknown', label: subCode || '—' }
  const parent = sub.parent_id ? maps.byId.get(sub.parent_id) : null
  if (parent && parent.level === 4) return { key: normSC(parent.code), kind: 'resource', label: parent.code }
  if (parent && parent.level === 1) return { key: normSC(parent.code), kind: 'legacy', label: parent.code }
  return { key: null, kind: 'unknown', label: subCode || '—' }
}

// Indirect: cost_code_id (uuid, preferred) with sub_code (text) fallback
// when cost_code_id is NULL -- confirmed 29% of existing indirect PO
// lines rely on this fallback. Priority is strict if/else (never both),
// preventing any double-count risk. Not used by PrintDocument.jsx
// currently (PRs are always Direct), but centralized here so any future
// Indirect PR/print path reuses the exact same rule instead of
// reintroducing a new copy.
export function resolveIndirectKey(costCodeId, subCode, maps) {
  let node = costCodeId ? maps.byId.get(costCodeId) : null
  if (node && node.level === 2) node = node.parent_id ? maps.byId.get(node.parent_id) : null // rare 0.8% miscategorized case
  if (node && node.level === 1) return { key: normSC(node.code), kind: 'costcode', label: node.code }

  const sub = subCode ? maps.byNormCode.get(normSC(subCode)) : null
  const parent = sub?.parent_id ? maps.byId.get(sub.parent_id) : null
  if (parent && parent.level === 1) return { key: normSC(parent.code), kind: 'costcode', label: parent.code }
  return { key: null, kind: 'unknown', label: subCode || '—' }
}

// "Direct" = "anything not indirect", including NULL/undefined cost_type
// (nullable column, 'direct' DEFAULT only applies on INSERT without an
// explicit value -- NULL is a real possible value that SQL .neq() would
// silently exclude). Always apply in JS after fetch, never as .neq().
export const isDirectPO = (po) => po?.cost_type !== 'indirect'

// Builds the { byId, byNormCode } lookup maps from a flat cost_codes
// rows array (id, code, level, parent_id only). Callers fetch the rows
// themselves (from state, cache, or a fresh query) — this module has no
// Supabase dependency, keeping it a pure, easily-testable logic module.
export function buildCostCodeMaps(costCodesRows) {
  const byId = new Map((costCodesRows || []).map(c => [c.id, c]))
  const byNormCode = new Map((costCodesRows || []).map(c => [normSC(c.code), c]))
  return { byId, byNormCode }
}

// resolveHierarchyKey() -- shared hierarchy-based Direct/Indirect
// resolver for PR Budget Comparison (Material + Equipment) ONLY. Added
// per the approved business rule that the Cost Code hierarchy itself
// (not purchase_orders.cost_type) is the source of truth for Budget
// classification. Deliberately does NOT replace or modify
// resolveDirectKey()/resolveIndirectKey()/isDirectPO() above, which
// remain the exclusive, untouched basis for the existing PO financial
// Dashboard and Audit/Evidence Modal (value-based, cost_type-driven,
// out of scope for this change).
//
// Classification is purely structural, based on the Sub Code's
// immediate parent in the cost_codes tree:
//   parent.level === 4 (Resource Code) -> 'direct'
//   parent.level === 1 (Cost Code)     -> 'indirect'
//   anything else                       -> 'unknown' (no comparison)
//
// budgetSource/budgetField are metadata for callers to know which
// table/column to aggregate against -- this function does not query
// the database itself.
export function resolveHierarchyKey(subCode, maps) {
  const sub = maps.byNormCode.get(normSC(subCode))
  if (!sub) return { kind: 'unknown', key: null, label: subCode || '—' }
  const parent = sub.parent_id ? maps.byId.get(sub.parent_id) : null
  if (parent && parent.level === 4) {
    return { kind: 'direct', key: normSC(parent.code), label: parent.code, budgetSource: 'tender_direct_items', budgetField: 'resource_code' }
  }
  if (parent && parent.level === 1) {
    return { kind: 'indirect', key: normSC(parent.code), label: parent.code, budgetSource: 'tender_indirect_items', budgetField: 'cost_code' }
  }
  return { kind: 'unknown', key: null, label: subCode || '—' }
}

